import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import Navbar from '../components/common/Navbar';
import { useNavigate } from 'react-router-dom';

const Settings = () => {
  const { user } = useAuth();
  // Platshållare tills serverns värden hämtats. Speglar UserSettings-schemats
  // defaults — stod e-postraderna tidigare på false, vilket visade "av" en kort
  // stund för någon som i själva verket hade dem på.
  const [settings, setSettings] = useState({
    notifications: {
      shoppingList: {
        onChange: true,
        onDelete: true
      },
      rumsbeskrivning: {
        onChange: true,
        onDelete: true
      },
      timeline: {
        onChange: true,
        onDelete: true
      }
    },
    emailNotifications: {
      enabled: true,
      shoppingList: {
        onChange: true,
        onDelete: true
      },
      rumsbeskrivning: {
        onChange: true,
        onDelete: true
      },
      timeline: {
        onChange: true,
        onDelete: true,
        onReminder: true
      }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  // Kontrollera om användaren är en byggkonsult
  const isConsultant = user?.role === 'byggkonsult';

  // Roller som har tillgång till Vecka-vyn — bara dessa får välja startvy
  const VECKA_ROLES = ['byggkonsult', 'inredningsarkitekt', 'admin', 'byggare'];
  const canChooseLandingView = VECKA_ROLES.includes(user?.role);
  const [landingView, setLandingView] = useState(user?.defaultLandingView || 'oversikt');
  const [savingLanding, setSavingLanding] = useState(false);

  // Sätt initialvärdet när user-objektet kommer in
  useEffect(() => {
    if (user?.defaultLandingView) setLandingView(user.defaultLandingView);
  }, [user?.defaultLandingView]);

  const handleLandingViewChange = async (newValue) => {
    if (!user?._id || newValue === landingView) return;
    const previous = landingView;
    setLandingView(newValue); // optimistisk uppdatering
    setSavingLanding(true);
    try {
      // Använd preferens-endpointen — kräver inte admin-access (PUT /users/:id gör det)
      await api.patch('/users/me/preferences', { defaultLandingView: newValue });
      // Uppdatera localStorage så PrivateRoute plockar upp värdet utan reload
      localStorage.setItem('defaultLandingView', newValue);
    } catch (err) {
      console.error('Kunde inte spara startvy:', err);
      setLandingView(previous);
      alert('Kunde inte spara startvy: ' + (err.message || 'okänt fel'));
    } finally {
      setSavingLanding(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // fetchSettings utelämnad med avsikt: den definieras nedanför i filen, och att
    // referera den här läses under renderingen innan const tilldelats — alltså
    // ReferenceError. Effekten kör på de värden som faktiskt styr den.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data || settings);
      setLoading(false);
    } catch (error) {
      console.error('Kunde inte hämta inställningar:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleSettingChange = (category, type, value, isEmail = false) => {
    const settingKey = isEmail ? 'emailNotifications' : 'notifications';
    const pathPrefix = isEmail ? 'emailNotifications' : 'notifications';
    
    setPendingChanges(prev => ({
      ...prev,
      [`${pathPrefix}.${category}.${type}`]: value
    }));
    
    setUnsavedChanges(true);
    
    setSettings(prevSettings => ({
      ...prevSettings,
      [settingKey]: {
        ...prevSettings[settingKey],
        [category]: {
          ...prevSettings[settingKey][category],
          [type]: value
        }
      }
    }));
  };

  const handleEmailEnabledChange = (value) => {
    setPendingChanges(prev => ({
      ...prev,
      'emailNotifications.enabled': value
    }));
    
    setUnsavedChanges(true);
    
    setSettings(prevSettings => ({
      ...prevSettings,
      emailNotifications: {
        ...prevSettings.emailNotifications,
        enabled: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      for (const [path, value] of Object.entries(pendingChanges)) {
        await api.patch('/settings', { path, value });
      }
      
      await fetchSettings();
      
      setPendingChanges({});
      setUnsavedChanges(false);
      setShowSaveConfirmation(true);
    } catch (error) {
      console.error('Kunde inte spara inställningar:', error);
      alert('Ett fel uppstod när inställningarna skulle sparas.');
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar currentUser={user} />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p>Laddar inställningar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar currentUser={user} />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center text-red-600">
            <p>Ett fel uppstod: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar currentUser={user} />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-leadme-pink-dark"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Tillbaka till projektöversikten
          </button>
        </div>
        
        {canChooseLandingView && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-1">Startvy</h2>
            <p className="text-sm text-gray-500 mb-4">
              Välj vilken vy som öppnas direkt när du loggar in.
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="defaultLandingView"
                  value="oversikt"
                  checked={landingView === 'oversikt'}
                  onChange={() => handleLandingViewChange('oversikt')}
                  disabled={savingLanding}
                  className="w-4 h-4 text-leadme-pink focus:ring-leadme-pink"
                />
                <span className="text-sm">
                  <span className="font-medium">Översikt</span>
                  <span className="text-gray-500 ml-2">— pipeline med dina projekt</span>
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="defaultLandingView"
                  value="vecka"
                  checked={landingView === 'vecka'}
                  onChange={() => handleLandingViewChange('vecka')}
                  disabled={savingLanding}
                  className="w-4 h-4 text-leadme-pink focus:ring-leadme-pink"
                />
                <span className="text-sm">
                  <span className="font-medium">Vecka</span>
                  <span className="text-gray-500 ml-2">— dagens och veckans möten + uppgifter</span>
                </span>
              </label>
            </div>
            {savingLanding && (
              <p className="text-xs text-gray-400 mt-2">Sparar...</p>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vänster kolumn - Notifieringsinställningar */}
            <div>
              <h2 className="text-xl font-semibold mb-6">Notifieringsinställningar</h2>
              
              <div className="space-y-6">
                {/* Produktval */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-medium mb-3">Produktval</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.shoppingList.onChange}
                        onChange={(e) => handleSettingChange('shoppingList', 'onChange', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid ändringar i produktvalslista</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.shoppingList.onDelete}
                        onChange={(e) => handleSettingChange('shoppingList', 'onDelete', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid borttagning av produktvalslista</span>
                    </label>
                  </div>
                </div>

                {/* Rumsbeskrivning */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-medium mb-3">Rumsbeskrivning</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.rumsbeskrivning.onChange}
                        onChange={(e) => handleSettingChange('rumsbeskrivning', 'onChange', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid ändringar i rumsbeskrivning</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.rumsbeskrivning.onDelete}
                        onChange={(e) => handleSettingChange('rumsbeskrivning', 'onDelete', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid borttagning av rumsbeskrivning</span>
                    </label>
                  </div>
                </div>

                {/* Tidplan */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Tidplan</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.timeline.onChange}
                        onChange={(e) => handleSettingChange('timeline', 'onChange', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid nya start- eller slutdatum</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.notifications.timeline.onDelete}
                        onChange={(e) => handleSettingChange('timeline', 'onDelete', e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600"
                      />
                      <span className="ml-2">Vid borttagning av tidplan</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Höger kolumn - E-postaviseringar */}
            <div className="border-t md:border-t-0 md:border-l pt-6 md:pt-0 md:pl-6">
              <h2 className="text-xl font-semibold mb-6">E-postaviseringar</h2>
              
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications?.enabled}
                    onChange={(e) => handleEmailEnabledChange(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600"
                  />
                  <span className="ml-2 font-medium">Aktivera e-postaviseringar</span>
                </label>
              </div>
              
              <div className="space-y-6">
                {/* Produktval */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-medium mb-3">Produktval</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.shoppingList?.onChange}
                        onChange={(e) => handleSettingChange('shoppingList', 'onChange', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid ändringar i produktvalslista</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.shoppingList?.onDelete}
                        onChange={(e) => handleSettingChange('shoppingList', 'onDelete', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid borttagning av produktvalslista</span>
                    </label>
                  </div>
                </div>

                {/* Rumsbeskrivning */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-medium mb-3">Rumsbeskrivning</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.rumsbeskrivning?.onChange}
                        onChange={(e) => handleSettingChange('rumsbeskrivning', 'onChange', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid ändringar i rumsbeskrivning</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.rumsbeskrivning?.onDelete}
                        onChange={(e) => handleSettingChange('rumsbeskrivning', 'onDelete', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid borttagning av rumsbeskrivning</span>
                    </label>
                  </div>
                </div>

                {/* Tidplan */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Tidplan</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.timeline?.onChange}
                        onChange={(e) => handleSettingChange('timeline', 'onChange', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid nya start- eller slutdatum</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications?.timeline?.onDelete}
                        onChange={(e) => handleSettingChange('timeline', 'onDelete', e.target.checked, true)}
                        disabled={!settings.emailNotifications?.enabled}
                        className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                      />
                      <span className="ml-2">Vid borttagning av tidplan</span>
                    </label>
                    {isConsultant && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications?.timeline?.onReminder}
                          onChange={(e) => handleSettingChange('timeline', 'onReminder', e.target.checked, true)}
                          disabled={!settings.emailNotifications?.enabled}
                          className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                        />
                        <span className="ml-2">Vid notiser i tidplan</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Projekt */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-lg font-medium mb-3">Projekt</h3>
                  <div className="space-y-3">
                    {/* "Vid statusändringar" och "Vid ändringar av ansvariga" låg
                        här tidigare. Ingen kod skickade de typerna — rutorna gick
                        att kryssa i, valet sparades, och sedan hände ingenting. */}
                    {['byggkonsult', 'inredningsarkitekt'].includes(user?.role) && (
                      <label className="flex items-center">
                        {/* Gick tidigare förbi handleSettingChange: rutan ändrades
                            på skärmen men lades aldrig i pendingChanges, så
                            "Spara" skickade ingenting och valet försvann. */}
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications?.project?.onReadyToStart}
                          onChange={(e) => handleSettingChange('project', 'onReadyToStart', e.target.checked, true)}
                          disabled={!settings.emailNotifications?.enabled}
                          className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                        />
                        <span className="ml-2">När kund klarmarkerat produktval och rumsbeskrivning</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* PU — bara för dem som skickar PU */}
                {['byggkonsult', 'admin'].includes(user?.role) && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-medium mb-3">PU</h3>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          // Saknas fältet (äldre inställningar) gäller standard: på
                          checked={settings.emailNotifications?.pu?.onResponse !== false}
                          onChange={(e) => handleSettingChange('pu', 'onResponse', e.target.checked, true)}
                          disabled={!settings.emailNotifications?.enabled}
                          className="rounded border-gray-300 text-indigo-600 disabled:opacity-50"
                        />
                        <span className="ml-2">När en byggare godkänner eller avböjer ett PU</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-start">
            <button
              onClick={handleSave}
              disabled={!unsavedChanges}
              className={`px-4 py-2 rounded-md text-white ${
                unsavedChanges 
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {unsavedChanges ? 'Spara ändringar' : 'Inga ändringar att spara'}
            </button>
          </div>
        </div>
      </div>

      {showSaveConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-[400px]">
            <div className="flex flex-col items-center">
              <div className="rounded-full bg-green-100 p-3 mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-600">Sparad!</h3>
              <p className="text-gray-600 mt-2">Inställningarna har sparats</p>
              <button
                onClick={() => {
                  setShowSaveConfirmation(false);
                  setTimeout(() => {
                    navigate(-1);
                  }, 1000); 
                }}
                className="mt-6 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 